import type {
  ProgressPreferences,
  ProgressState,
} from "../application/progress-state";
import type { SelectedSoundtrackId } from "../config/soundtrack-config";
import {
  observeProgressMutation,
  type ProgressMutation,
} from "../application/progress-mutation";
import { DialogFocus } from "./dialog-focus";
import { SoundtrackSelect } from "./soundtrack-select";

export interface ParentSettingsStore {
  getState(): ProgressState;
  reset(confirmed: boolean): ProgressMutation<boolean>;
  update(
    createNextState: (state: ProgressState) => ProgressState,
  ): ProgressMutation<boolean>;
}

export interface ParentSettingsElements {
  readonly buildVersion: HTMLElement;
  readonly cancelResetButton: HTMLButtonElement;
  readonly confirmResetButton: HTMLButtonElement;
  readonly confirmation: HTMLElement;
  readonly hapticsButton: HTMLButtonElement;
  readonly musicButton: HTMLButtonElement;
  readonly reducedMotionButton: HTMLButtonElement;
  readonly resetButton: HTMLButtonElement;
  readonly soundButton: HTMLButtonElement;
  readonly soundtrackSelect: HTMLSelectElement;
  readonly status: HTMLElement;
}

type PreferenceName = keyof ProgressPreferences;

const requireElement = (documentScope: Document, id: string): HTMLElement => {
  const element = documentScope.getElementById(id);
  if (element === null) {
    throw new Error(`Missing parent settings element: ${id}`);
  }
  return element;
};

export class ParentSettingsShell {
  private mutationPending = false;
  private resetPending = false;
  private readonly soundtrackSelect: SoundtrackSelect;

  public constructor(
    private readonly store: ParentSettingsStore,
    private readonly elements: ParentSettingsElements,
    version: string,
    private readonly resetDialogFocus: DialogFocus,
    private readonly handlePreferenceChange: () => void = () => undefined,
  ) {
    elements.buildVersion.textContent = `Installed ${version}`;
    elements.hapticsButton.addEventListener("click", this.handleHaptics);
    elements.musicButton.addEventListener("click", this.handleMusic);
    elements.soundButton.addEventListener("click", this.handleSound);
    elements.reducedMotionButton.addEventListener(
      "click",
      this.handleReducedMotion,
    );
    elements.resetButton.addEventListener("click", this.handleResetStart);
    elements.cancelResetButton.addEventListener(
      "click",
      this.handleResetCancel,
    );
    elements.confirmResetButton.addEventListener(
      "click",
      this.handleResetConfirm,
    );
    this.soundtrackSelect = new SoundtrackSelect(
      elements.soundtrackSelect,
      store.getState().selectedSoundtrackId,
      this.selectSoundtrack,
    );
    this.render();
  }

  public destroy(): void {
    this.elements.hapticsButton.removeEventListener(
      "click",
      this.handleHaptics,
    );
    this.elements.musicButton.removeEventListener("click", this.handleMusic);
    this.elements.soundButton.removeEventListener("click", this.handleSound);
    this.elements.reducedMotionButton.removeEventListener(
      "click",
      this.handleReducedMotion,
    );
    this.elements.resetButton.removeEventListener(
      "click",
      this.handleResetStart,
    );
    this.elements.cancelResetButton.removeEventListener(
      "click",
      this.handleResetCancel,
    );
    this.elements.confirmResetButton.removeEventListener(
      "click",
      this.handleResetConfirm,
    );
    this.soundtrackSelect.destroy();
    this.resetDialogFocus.destroy();
  }

  public close(): void {
    this.resetPending = false;
    this.elements.status.textContent = "";
    this.render();
  }

  public render(): void {
    this.elements.confirmation.hidden = !this.resetPending;
    if (this.resetPending) {
      this.resetDialogFocus.activate();
    } else {
      this.resetDialogFocus.deactivate();
    }
    const state = this.store.getState();
    const preferences = state.preferences;
    this.elements.confirmResetButton.disabled = this.mutationPending;
    this.elements.hapticsButton.disabled = this.mutationPending;
    this.elements.musicButton.disabled = this.mutationPending;
    this.elements.reducedMotionButton.disabled = this.mutationPending;
    this.elements.resetButton.disabled = this.mutationPending;
    this.elements.soundButton.disabled = this.mutationPending;
    this.elements.soundtrackSelect.disabled = this.mutationPending;
    this.soundtrackSelect.render(state.selectedSoundtrackId);
    this.renderPreference(
      this.elements.hapticsButton,
      "Haptics",
      preferences.hapticsEnabled,
    );
    this.renderPreference(
      this.elements.musicButton,
      "Music",
      preferences.musicEnabled,
    );
    this.renderPreference(
      this.elements.soundButton,
      "Sound",
      preferences.soundEnabled,
    );
    this.renderPreference(
      this.elements.reducedMotionButton,
      "Reduced motion",
      preferences.reducedMotion,
    );
  }

  private readonly handleMusic = (): void => {
    this.togglePreference("musicEnabled", "Music");
  };

  private readonly handleHaptics = (): void => {
    this.togglePreference("hapticsEnabled", "Haptics");
  };

  private readonly handleReducedMotion = (): void => {
    this.togglePreference("reducedMotion", "Reduced motion");
  };

  private readonly handleResetCancel = (): void => {
    this.resetPending = false;
    this.elements.status.textContent = "Progress reset canceled.";
    this.render();
  };

  private readonly handleResetConfirm = (): void => {
    this.observeBooleanMutation(
      this.store.reset(true),
      "Resetting progress…",
      (saved) => {
        if (saved) {
          this.resetPending = false;
          this.elements.status.textContent =
            "Snakely progress reset to defaults.";
          this.handlePreferenceChange();
        } else {
          this.elements.status.textContent =
            "Progress was not reset. Your saved progress is unchanged.";
        }
      },
    );
  };

  private readonly handleResetStart = (): void => {
    this.resetPending = true;
    this.elements.status.textContent = "";
    this.render();
  };

  private readonly handleSound = (): void => {
    this.togglePreference("soundEnabled", "Sound");
  };

  private readonly selectSoundtrack = (
    id: SelectedSoundtrackId,
  ): ProgressMutation<boolean> => {
    const mutation = this.store.update((state) => ({
      ...state,
      selectedSoundtrackId: id,
    }));
    this.observeBooleanMutation(mutation, "Saving soundtrack…", (saved) => {
      this.elements.status.textContent = saved
        ? "Soundtrack saved."
        : "Soundtrack was not saved.";
      if (saved) {
        this.handlePreferenceChange();
      }
    });
    return mutation;
  };

  private renderPreference(
    button: HTMLButtonElement,
    label: "Haptics" | "Music" | "Reduced motion" | "Sound",
    enabled: boolean,
  ): void {
    button.setAttribute("aria-pressed", String(enabled));
    button.textContent = `${label} ${enabled ? "on" : "off"}`;
  }

  private togglePreference(
    preference: PreferenceName,
    label: "Haptics" | "Music" | "Reduced motion" | "Sound",
  ): void {
    const mutation = this.store.update((state) => ({
      ...state,
      preferences: {
        ...state.preferences,
        [preference]: !state.preferences[preference],
      },
    }));
    this.observeBooleanMutation(
      mutation,
      `Saving ${label.toLowerCase()} preference…`,
      (saved) => {
        this.elements.status.textContent = saved
          ? `${label} preference saved.`
          : `${label} preference was not saved.`;
        if (saved) {
          this.handlePreferenceChange();
        }
      },
    );
  }

  private observeBooleanMutation(
    mutation: ProgressMutation<boolean>,
    pendingCopy: string,
    complete: (saved: boolean) => void,
  ): void {
    observeProgressMutation(mutation, {
      complete: (saved) => {
        this.mutationPending = false;
        complete(saved);
        this.render();
      },
      failed: () => {
        this.mutationPending = false;
        complete(false);
        this.render();
      },
      pending: () => {
        this.mutationPending = true;
        this.elements.status.textContent = pendingCopy;
        this.render();
      },
    });
  }
}

export const installParentSettingsShell = (
  documentScope: Document,
  store: ParentSettingsStore,
  version: string,
  handlePreferenceChange?: () => void,
): ParentSettingsShell => {
  const elements: ParentSettingsElements = {
    buildVersion: requireElement(documentScope, "parent-build-version"),
    cancelResetButton: requireElement(
      documentScope,
      "parent-reset-cancel-button",
    ) as HTMLButtonElement,
    confirmResetButton: requireElement(
      documentScope,
      "parent-reset-confirm-button",
    ) as HTMLButtonElement,
    confirmation: requireElement(documentScope, "parent-reset-confirmation"),
    hapticsButton: requireElement(
      documentScope,
      "parent-haptics-button",
    ) as HTMLButtonElement,
    musicButton: requireElement(
      documentScope,
      "parent-music-button",
    ) as HTMLButtonElement,
    reducedMotionButton: requireElement(
      documentScope,
      "parent-reduced-motion-button",
    ) as HTMLButtonElement,
    resetButton: requireElement(
      documentScope,
      "parent-reset-button",
    ) as HTMLButtonElement,
    soundButton: requireElement(
      documentScope,
      "parent-sound-button",
    ) as HTMLButtonElement,
    soundtrackSelect: requireElement(
      documentScope,
      "parent-soundtrack-select",
    ) as HTMLSelectElement,
    status: requireElement(documentScope, "parent-settings-status"),
  };
  return new ParentSettingsShell(
    store,
    elements,
    version,
    new DialogFocus(
      documentScope,
      elements.cancelResetButton,
      [elements.cancelResetButton, elements.confirmResetButton],
      elements.resetButton,
    ),
    handlePreferenceChange,
  );
};
